import React, { FC, useState, useContext } from 'react';
import injectSheet from 'react-jss';
import CheckBox from 'Components/CheckBox';
import { SearchBar } from 'Components/SearchBar';
import { SelectList } from 'Components/Lists';
import { Button } from 'Components/Buttons';
import { Paging } from 'Components/Paging';
import { Loading } from 'Components/Loading';
import { Input } from 'Components/Inputs';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import styleSheet from './styles';

interface Props {
  disabled?: boolean;
  loading?: boolean;
  classes?: any;
  errorMessage?: string;
  onCancel?: () => void;
  onSearch?: (e: any) => void;
  onCreate?: (e: object) => void;
  closeModal?: () => void;
  listData?: { list: any[] };
  columns: string[];
  allColumns: string[];
  selectedList: any;
  setSelectedList: (val: any) => void;
  // Paging //
  page: number;
  recordsPerPage: number;
  pagingTotalRecords?: number;
  pagingNextPage?: () => void;
  pagingPrevPage?: () => void;
  sort?: any;
  checkedItems?: any;
  totalPages?: number;
}

const ExistingList: FC<Props> = (props: Props) => {
  const [rename, setRename] = useState(false);
  const [renameInput, setRenameInput] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [search, setSearch] = useState('');
  const [prevSearch, setPrevSearch] = useState('');
  const [error, setError] = useState('');
  const { location } = useRouter();
  const {
    helpers: {
      View,
      selectedFilters,
      metrics: { store: metricsStore },
    },
    actions: { track },
  } = useContext(ProductAnalyticsContext);

  const {
    disabled,
    loading,
    classes,
    errorMessage,
    onCancel,
    onSearch,
    onCreate,
    closeModal,
    listData,
    columns,
    allColumns,
    selectedList,
    setSelectedList,
    page,
    recordsPerPage,
    pagingTotalRecords,
    pagingNextPage,
    pagingPrevPage,
    sort,
    checkedItems,
    totalPages,
  } = props;

  const [time] = useState(new Date().getTime()); //

  const cancel = () => {
    // Combined Analytics event tracking
    const { listTotalCount }: any = metricsStore;
    track('add_to_list_cancelled', {
      screen: View(location.pathname),
      scope: checkedItems && checkedItems.length === 0 ? 'All' : 'Selected',
      existing_list_count: listTotalCount,
    });

    if (onCancel) onCancel();
    if (closeModal) closeModal();
  };

  const onClick = () => {
    // Combined Analytics event tracking
    const scope =
      checkedItems && checkedItems.length === 0 ? 'All' : 'Selected';
    const { listTotalCount }: any = metricsStore;
    track('add_to_list_submitted', {
      screen: View(location.pathname),
      scope,
      existing_list_count: listTotalCount, // This is the number of lists created by a user
      new_list_created: false, // This is an existing list.
      time_spent: `${Math.abs((new Date().getTime() - time) / 1000)} sec`, // Convert the milliseconds to seconds
      filters_selected: selectedFilters().join(','),
    });

    track('existing_list_tab_viewed', {
      screen: View(location.pathname),
      scope,
      existing_list_count: listTotalCount,
    });
    if (onCreate)
      onCreate({
        selection: selectedList,
        renameValue: renameInput,
        overwrite,
      });
  };
  const searchClick = (e: any) => {
    onSearch(e);
    setPrevSearch(e.target.value.length >= 3 ? e.target.value : '');
    setError(
      e.target.value.length > 0 &&
        e.target.value.length < 3 &&
        '[at least 3 characters required to search]'
    );
  };

  const renameClick = () => setRename(!rename);
  const overWriteClick = () => setOverwrite(!overwrite);
  const renameInputChange = (e: any) => setRenameInput(e.target.value);
  const selectedListChange = (e: any) => setSelectedList(e.target.lead);
  const setSearchChange = (e: any) => {
    setSearch(e.target.value);
    setError('');
  };

  return (
    <div className={classes.container}>
      <SearchBar
        name="Search Lists"
        disabled={listData.list.length === 0}
        disableToolTip
        placeholder="Search by Name"
        width={190}
        fields={["'List Name'"]}
        searchable={
          (search.length >= 3 && prevSearch !== search && true) || false
        }
        onSearch={searchClick}
        onChange={setSearchChange}
        error={error}
      />
      <div
        style={{
          height: listData.list.length > 0 ? '378px' : 'auto',
          position: 'relative',
        }}
      >
        <div style={{ opacity: loading ? 0.4 : 1 }}>
          <SelectList
            onChange={selectedListChange}
            isChecked={selectedList}
            audienceData={listData}
            tableColumns={columns}
            allColumns={allColumns}
            sort={sort}
          />
        </div>
        {loading && (
          <div className={classes.loadingHolder}>
            <Loading />
          </div>
        )}
      </div>

      {listData.list.length > 0 && (
        <div style={{ transform: 'translateX(35px) translateY(-10px)' }}>
          <Paging
            page={page}
            recordsPerPage={recordsPerPage}
            totalRecords={pagingTotalRecords}
            prev={pagingPrevPage}
            next={pagingNextPage}
            position="right"
            parent=" Add to List"
            totalPages={totalPages}
          />
        </div>
      )}

      <div className={classes.bottom}>
        <div className={classes.checkHolder}>
          <CheckBox
            label="Rename List"
            isChecked={rename}
            onClick={renameClick}
          />
          <div
            style={{
              height: '40px',
              marginLeft: '32px',
              width: '100%',
              position: 'relative',
            }}
          >
            {rename && (
              <>
                <Input
                  type="text"
                  name="renameList"
                  placeholder="New list name"
                  onChange={renameInputChange}
                  label=""
                  limitCharacterText="100 characters max"
                  maxLength={100}
                  textInput={renameInput}
                />
                {errorMessage && (
                  <div className={classes.error}>{errorMessage}</div>
                )}
              </>
            )}
          </div>
        </div>
        <div className={classes.checkHolder}>
          <CheckBox
            label="Overwrite List"
            isChecked={overwrite}
            onClick={overWriteClick}
          />
        </div>
      </div>
      <div className={classes.controls}>
        <Button onClick={cancel} buttonType="defaultClear">
          Cancel
        </Button>
        <Button
          disabled={
            (rename && renameInput.length < 1) ||
            disabled ||
            listData.list.length === 0
          }
          onClick={onClick}
          buttonType="defaultBlue"
        >
          Add to List
        </Button>
      </div>
    </div>
  );
};

ExistingList.defaultProps = {
  disabled: false,
  loading: true,
  classes: {},
  errorMessage: '',
  onCancel: () => {},
  onSearch: () => {},
  onCreate: () => {},
  closeModal: () => {},
  listData: { list: [] },
  pagingTotalRecords: 0,
  pagingNextPage: () => {},
  pagingPrevPage: () => {},
  sort: {},
  checkedItems: [],
  totalPages: 0,
};
export default injectSheet(styleSheet)(ExistingList);
